import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import InputBox, { DropDown, RadioButtons, SearchInput } from 'components/inputBox/inputBox';
import businessEstablishmentData from '../../dummy-data/businessEstablishment.json';
import Building from '../../assets/images/building.svg';
import './businessEstablishmentSettings.scss';
import {
  STORE_ADDR_TYPE,
  STORE_ALLOW_COMMUTE_TIME,
  STORE_CALCULATE_DAY,
  STORE_DISTANCE,
  STORE_TYPE,
} from 'constants/CONSTATNS';
import SearchAddrMap from 'common/SearchAddrMap';
import PopupFrame from 'layouts/PopupFrame';
import useModalStore from 'zustands/useAlertConfirmStore';
import useErrorLog from 'hooks/useErrorLog';
import { AxiosResponse } from 'axios';
import useApi from 'hooks/useApi';
import { formatValueToNumber, onlyNumberInput } from 'constants/utils';
import useLoadingStore from 'zustands/useLoadingStore';
import useStoreStore from 'zustands/useStoreStore';

interface IStoreForm {
  NAME: string;
  BRANCHNAME: string;
  ADDR1: string;
  ADDR2: string;
  LAT: number;
  LONG: number;
  DISTANCE: number;
  CALCULATE_DAY: string;
  LATE_FLAG: string;
  LATE_TIME: number;
  EARLY_FLAG: string;
  EARLY_TIME: number;
  CATEGORY: string;
  other: string;
  TYPE: number;
  PAY_DAY: string;
  CLOSE_FLAG?: string;
}

export const getStoreCategoryList = () => {
  return ['요식업(음식점,카페 등)', '도,소매업(편의점 등)', '서비스업(pc방, 헬스장 등)', '일반회사', '기타'];
};
export const getStoreType = () => {
  return ['영업', '폐업'];
};
export const getStoreAllowDistance = () => {
  return ['150m', '200m', '500m', '1km', '1.5km', '2km', '4km', '재택근무 (거리 제한 없음)'];
};
export const getStoreCommuteAllowTime = () => {
  return ['0분', '5분', '10분', '15분', '30분', '60분'];
};
export const getDayWithText = () => {
  const dayList = [];
  for (let i = 1; i < 30; i++) {
    dayList.push(`${i}일`);
  }
  dayList.push(`말일`);
  return dayList;
};

const businessEstablishmentSettings = ({ type }: { type: 'create' | 'update' }) => {
  const navigate = useNavigate();
  const { setModalState } = useModalStore();
  const { setLoadingState } = useLoadingStore();
  const { storeSeq, setStoreInfo, storeSeqNumber } = useStoreStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const formTypeName = type === 'update' ? '수정' : '추가';
  const [store_name, setStore_name] = useState<string>('');
  const [store_sub_name, setStore_sub_name] = useState<string>('');
  const [storeAddrType, setStoreAddrType] = useState<STORE_ADDR_TYPE>('basic');
  const [storeLat, setStoreLat] = useState<number>(0);
  const [storeLong, setStoreLong] = useState<number>(0);
  const [addr, setAddr] = useState<string>('');
  const [addr_sub, setAddr_sub] = useState<string>('');
  const [showAddrSearch, setShowAddrSearch] = useState<boolean>(false);
  const [store_category, setStore_category] = useState<STORE_TYPE>('요식업(음식점,카페 등)');
  const [store_category_etc, setStore_category_etc] = useState<string>('');
  const [store_type, setStore_type] = useState<'영업' | '폐업'>('영업');

  const [store_distance, setStore_distance] = useState<STORE_DISTANCE | string>('150m');
  const [lateness_time, setLateness_time] = useState<STORE_ALLOW_COMMUTE_TIME | string>('0분');
  const [early_time, setEarly_time] = useState<STORE_ALLOW_COMMUTE_TIME | string>('0분');

  const [store_scale, setStore_scale] = useState<'5인 미만' | '5인 이상'>('5인 미만');
  const [store_calculate_day, setStore_calculate_day] = useState<STORE_CALCULATE_DAY | string>('말일');
  const [store_pay_day, setStore_pay_day] = useState<STORE_CALCULATE_DAY | string>('말일');

  const fetchData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getStore`, {
        STORE_SEQ: storeSeq,
      });
      console.log('/auth/getStore_data', data);

      if (data.resultCode === 0) {
        const {
          NAME,
          branchName,
          LAT,
          LONG,
          ADDR1,
          ADDR2,
          CATEGORY,
          other,
          JULI,
          LATE_TIME,
          EARLY_TIME,
          TYPE,
          CALCULATE_DAY,
          PAY_DAY,
        } = data.resultData[0];

        setStore_name(NAME);
        setStore_sub_name(branchName);
        setStoreLat(Number(LAT));
        setStoreLong(Number(LONG));
        setAddr(ADDR1);
        setAddr_sub(ADDR2);
        setStore_category(CATEGORY);
        setStore_category_etc(other);
        setStore_distance(
          JULI === -1 ? '재택근무 (거리 제한 없음)' : Number(JULI) >= 1000 ? JULI / 1000 + 'km' : JULI + 'm',
        );
        setLateness_time(LATE_TIME + '분');
        setEarly_time(EARLY_TIME + '분');
        setStore_scale(TYPE === 1 ? '5인 이상' : '5인 미만');
        setStore_calculate_day(CALCULATE_DAY === '1' ? '말일' : Number(CALCULATE_DAY) - 1 + '일');
        setStore_pay_day(PAY_DAY === '1' ? '말일' : Number(PAY_DAY) - 1 + '일');
      } else {
        throw new Error('//auth/getStore');
      }
    } catch (err) {
      checkErrorLog(err, 'BusinessEstablishmentChoice /auth/getStore');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 주소찾기 클릭
  const toggleSearchAddr = (value: boolean) => {
    setShowAddrSearch(value);
  };

  // 사업장 분류 변경
  const handleChangeStoreCategory = useCallback(
    (value: STORE_TYPE) => {
      setStore_category(value);
      if (value === '기타') {
        setTimeout(() => {
          StoreCategoryEtcInputRef.current?.focus();
        }, 300);
      }
    },
    [store_category],
  );
  // 사업장 상태 변경
  const handleChangeStoreType = useCallback(
    (value: '영업' | '폐업') => {
      setStore_type(value);
    },
    [store_type],
  );
  // 출퇴근 허용 범위 변경
  const handleChangeStoreDistance = useCallback(
    (value: STORE_DISTANCE) => {
      setStore_distance(value);
    },
    [store_distance],
  );
  // 지각 허용 범위 변경
  const handleChangeStoreLateness = useCallback(
    (value: STORE_ALLOW_COMMUTE_TIME) => {
      setLateness_time(value);
    },
    [lateness_time],
  );
  // 조퇴 허용 범위 변경
  const handleChangeStoreEarly = useCallback(
    (value: STORE_ALLOW_COMMUTE_TIME) => {
      setEarly_time(value);
    },
    [early_time],
  );
  // 사업장 규모 변경
  const handleChangeStoreScale = useCallback(
    (value: string) => {
      if (value === '5인 미만' || value === '5인 이상') {
        setStore_scale(value);
      }
    },
    [store_scale],
  );
  // 급여정산일 변경
  const handleChangeCalculateDay = useCallback(
    (value: STORE_CALCULATE_DAY) => {
      setStore_calculate_day(value);
    },
    [store_calculate_day],
  );
  // 급여날 변경
  const handleChangePayDay = useCallback(
    (value: STORE_CALCULATE_DAY) => {
      setStore_pay_day(value);
    },
    [store_pay_day],
  );

  // 기본 주소 가져오기
  const getAddrFromMaps = (lat: number, long: number, addr: string, addrType: STORE_ADDR_TYPE) => {
    setStoreLat(lat);
    setStoreLong(long);
    setAddr(addr);
    setStoreAddrType(addrType);
    toggleSearchAddr(false);
  };

  // 입력 데이터 유효성 검증
  const checkSubmitData = () => {
    if (store_name.length === 0) return '사업장명을 입력해 주세요.';
    if (storeLat === 0 || storeLong === 0 || addr === '') return '주소찾기 버튼을 통해 기본 주소를 입력해 주세요.';
    if (addr_sub === '') return '상세 주소를 입력해 주세요.';
    if (store_category === '기타' && store_category_etc === '') return '기타 사업장 분류를 입력해 주세요.';
    return '';
  };

  // 저장하기 클릭
  const clickSubmitBtn = () => {
    if (store_type === '폐업') {
      setModalState({
        isModalShow: true,
        title: '사업장 수정',
        desc: '사업장을 폐업처리 하시겠습니까?',
        okBtnText: '확인',
        okCallback: submitStoreUpdate,
        isWarning: true,
      });
    } else {
      submitStoreUpdate();
    }
  };

  // 서업장 추가 및 수정
  const submitStoreUpdate = async () => {
    try {
      if (checkSubmitData() !== '') {
        return setModalState({ isModalShow: true, title: `사업장 ${formTypeName}`, desc: checkSubmitData() });
      } else {
        setLoadingState({ isFetchLoading: true });

        let formObj: IStoreForm = {
          NAME: store_name,
          BRANCHNAME: store_sub_name,
          ADDR1: addr,
          ADDR2: addr_sub,
          LAT: storeLat,
          LONG: storeLong,
          DISTANCE: store_distance.includes('재택') ? -1 : Number(formatValueToNumber(store_distance)),
          CALCULATE_DAY:
            store_calculate_day === '말일' ? '1' : (Number(onlyNumberInput(store_calculate_day)) + 1).toString(),
          LATE_FLAG: lateness_time === '0분' ? '0' : '1',
          LATE_TIME: Number(onlyNumberInput(lateness_time)),
          EARLY_FLAG: early_time === '0분' ? '0' : '1',
          EARLY_TIME: Number(onlyNumberInput(early_time)),
          CATEGORY: store_category,
          other: store_category === '기타' ? store_category_etc : '',
          TYPE: store_scale === '5인 이상' ? 1 : 0,
          PAY_DAY: store_pay_day === '말일' ? '1' : (Number(onlyNumberInput(store_pay_day)) + 1).toString(),
        };

        if (type === 'create') {
          const { data }: AxiosResponse['data'] = await webApiNode('post', '/auth/addStore', formObj);
          console.log('/auth/addStore data', data);
          if (data.resultCode === 0) {
            navigate(-1);
            setModalState({ isModalShow: true, title: '사업장 추가', desc: '완료되었습니다.' });
          } else {
            setModalState({ isModalShow: true, title: '사업장 추가', desc: '통신이 원활하지 않습니다.' });
            throw new Error('/auth/addStore');
          }
        } else {
          formObj = {
            ...formObj,
            CLOSE_FLAG: store_type === '영업' ? '0' : '1',
          };

          try {
            setLoadingState({ isFetchLoading: true });
            const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/editStore`, formObj);
            console.log('/auth/editStore_data', data);

            if (data.resultCode === 0) {
              if (formObj.CLOSE_FLAG === '1') {
                setStoreInfo({ storeSeq: '', storeName: '', storeSeqNumber: 0 });
                navigate('/business-establishment-choice');
                setModalState({ isModalShow: true, title: '사업장 수정', desc: '사업장이 폐업처리 되었습니다.' });
              } else {
                setStoreInfo({ storeSeq: storeSeq, storeName: formObj.NAME, storeSeqNumber: storeSeqNumber });
                setModalState({ isModalShow: true, title: '사업장 수정', desc: '완료되었습니다.' });
              }
            } else {
              throw new Error('/auth/editStore');
            }
          } catch (err) {
            checkErrorLog(err, 'BusinessEstablishmentChoice /auth/editStore');
          } finally {
            setLoadingState({ isFetchLoading: false });
          }
        }
      }
    } catch (err) {
      checkErrorLog(err, 'BusinessEstablishmentSettings submitStoreUpdate');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  useEffect(() => {
    if (type === 'update') fetchData();
  }, []);

  const StoreCategoryEtcInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="businessEstablishmentSettings">
      <PopupFrame onSubmit={clickSubmitBtn} onClose={() => {}}>
        <div className="heading">
          <div className="headingImage">
            <img src={Building} alt="" />
          </div>
          <div className="headingText">사업장 {formTypeName}</div>
        </div>

        <div className="container">
          <div className="firstSectionWrapper">
            <div className="leftContainer">
              <div className="boxHeader">사업자 정보</div>
              <InputBox
                label={'사업장명'}
                value={store_name}
                onChange={setStore_name}
                placeHolder={'사업장명을 입력해 주세요.'}
                maxLength={15}
              />
              <InputBox
                label={'지점명(선택)'}
                value={store_sub_name}
                onChange={setStore_sub_name}
                placeHolder={'지점명을 입력해 주세요.'}
                maxLength={15}
              />
              <SearchInput
                label={'기본 주소'}
                value={addr}
                onChange={setAddr}
                editable={false}
                placeHolder={'우측 주소찾기 버튼을 눌러주세요.'}
                onClick={() => toggleSearchAddr(true)}
              />
              {showAddrSearch === true && (
                <SearchAddrMap
                  storeLat={storeLat}
                  storeLong={storeLong}
                  storeAddrType={storeAddrType}
                  onClose={() => {
                    toggleSearchAddr(false);
                  }}
                  onSubmit={getAddrFromMaps}
                />
              )}
              <InputBox value={addr_sub} onChange={setAddr_sub} placeHolder={'상세 주소를 입력해 주세요.'} />
              <DropDown
                label={'사업장 분류'}
                value={store_category}
                options={getStoreCategoryList()}
                onSelect={handleChangeStoreCategory}
              />
              {store_category === '기타' && (
                <InputBox
                  value={store_category_etc}
                  ref={StoreCategoryEtcInputRef}
                  onChange={setStore_category_etc}
                  placeHolder="기타 사업장 분류를 입력해 주세요."
                />
              )}
              {storeSeq !== '' && (
                <DropDown
                  label={'사업장 상태'}
                  value={store_type}
                  options={getStoreType()}
                  onSelect={handleChangeStoreType}
                />
              )}
            </div>
            <div className="rightContainer">
              <div className="headingText ">사업장명</div>
              <div className="contextText contextOne">원하는 사업장이름으로 작성하세요</div>
              <div className="headingOne">기본주소</div>
              <div className="contextText contextTwo">GPS 기반으로 출퇴근 입력할 장소를 검색하세 요</div>
              <div className="headingText ">사업장 분류</div>
              <div className="contextText contextThree">해당하는 분류를 선택하세요</div>
              {storeSeq !== '' && (
                <>
                  <div className="headingText headingFour">사업장 상태</div>
                  <div className="contextText contextFour">현재 영업 상태를 선택하세요</div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="container">
          <div className="secondSectionWrapper">
            <div className="leftContainer">
              <div className="boxHeader">출퇴근 정보</div>
              <DropDown
                label={'출퇴근 허용범위'}
                options={getStoreAllowDistance()}
                value={store_distance}
                onSelect={handleChangeStoreDistance}
              />

              <DropDown
                label={'지각 허용범위'}
                options={getStoreCommuteAllowTime()}
                value={lateness_time}
                onSelect={handleChangeStoreLateness}
              />
              <DropDown
                label={'조퇴 허용범위'}
                options={getStoreCommuteAllowTime()}
                value={early_time}
                onSelect={handleChangeStoreEarly}
              />
            </div>
            <div className="rightContainer">
              <div className="headingText headingOne">출퇴근 허용범위</div>
              <div className="contextText contextOne">
                선택하신 거리 반경 내에서 출퇴근이 가능합니다. 150m~4km 사이에서 출퇴근 허용거리를 선택하세요
              </div>
              <div className="headingText headingTwo">기본 주소</div>
              <div className="contextText contextTwo">
                설정한 시간내 지각하여도 정상출퇴근으로 처리합니다. 단, 허용 시간 초과 경우에 지각시간
                (허용시간X초과시간)이 차감됩니다
              </div>
              <div className="headingText ">조퇴 허용범위</div>
              <div className="contextText contextThree">
                설정시간까지 지각해도 급여에서 참감되지 않습니다.단, 허용 시간 초과 경우에 지각시간
                (허용시간X초과시간)이 차감됩니다
              </div>
            </div>
          </div>
        </div>

        <div className="finalWrapper">
          <div className="container">
            <div className="thirdSectionWrapper">
              <div className="leftContainer">
                <div className="boxHeader">급여정보</div>

                <RadioButtons
                  header="사업장 규모"
                  items={['5인 미만', '5인 이상']}
                  value={store_scale}
                  onChange={handleChangeStoreScale}
                />
                <DropDown
                  label={'급여정산일'}
                  options={getDayWithText()}
                  value={store_calculate_day}
                  onSelect={handleChangeCalculateDay}
                />
                <DropDown
                  label={'급여날'}
                  options={getDayWithText()}
                  value={store_pay_day}
                  onSelect={handleChangePayDay}
                />
              </div>
              <div className="rightContainer">
                <div className="headingText headingOne">사업장 규모</div>
                <div className="contextText contextOne">
                  5인 이상 사업장 선택 시, 추가근무/야간근무 수당이 자동 가산됩니다
                </div>
                <div className="headingText ">급여정산일</div>
                <div className="contextText contextTwo">
                  급여가 계산되는 기간 설정입니다.급여지급일과 혼동하지 마세요.
                </div>
              </div>
            </div>
          </div>
        </div>
      </PopupFrame>
    </div>
  );
};

export default businessEstablishmentSettings;
